import { useCallback, useState } from 'react';
import { useApiFetch } from './useApiFetch';
import type { StopKind } from '../components/StopRunDialog';

/**
 * The two ways to stop an eval set, behind one call. Stop asks the runner to
 * score partial work and exit; Force stop deletes its Kubernetes resources.
 * Owner-only on the API; a 403 is reported as such rather than as a failure.
 */
export function useStopRun(
  jobId: string | null,
  onDone: (kind: StopKind) => void
) {
  const { apiFetch } = useApiFetch();
  const [busy, setBusy] = useState<StopKind | null>(null);
  const [error, setError] = useState<string | null>(null);

  const confirm = useCallback(
    async (kind: StopKind) => {
      if (jobId === null) return;
      setBusy(kind);
      setError(null);
      const id = encodeURIComponent(jobId);
      // Null on a network error or abort; apiFetch does not reject.
      const res = await apiFetch(
        kind === 'stop' ? `/eval_sets/${id}/stop` : `/eval_sets/${id}`,
        { method: kind === 'stop' ? 'POST' : 'DELETE' },
        { returnNonOkResponse: true }
      );
      setBusy(null);
      if (res && res.ok) {
        onDone(kind);
        return;
      }
      const verb = kind === 'stop' ? 'stop' : 'force stop';
      setError(
        res?.status === 403
          ? `You do not have permission to ${verb} this eval set.`
          : `Could not ${verb} the eval set${res ? ` (HTTP ${res.status})` : ''}.`
      );
    },
    [apiFetch, jobId, onDone]
  );

  const reset = useCallback(() => setError(null), []);
  return { busy, error, confirm, reset };
}
